import { useMemo, useCallback } from 'react'
import { useQuery, useMutation } from 'urql'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import type { PurchasePhoneNumberInput } from 'app/generated/graphql'
import {
  PHONE_NUMBERS_QUERY,
  PURCHASE_MUTATION,
  RELEASE_MUTATION,
  UPDATE_ROLES_MUTATION,
  SEARCH_AVAILABLE_QUERY,
} from './graphql'

export type PhoneNumberHit = ResultOf<typeof PHONE_NUMBERS_QUERY>['phoneNumbers']['hits'][number]
export type AvailablePhoneNumber = ResultOf<typeof SEARCH_AVAILABLE_QUERY>['searchAvailablePhoneNumbers'][number]

export type UseManagedNumbersReturn = {
  phoneNumbers: PhoneNumberHit[]
  fetching: boolean
  error: string | undefined
  searchAvailable: (type: 'VOICE' | 'MESSAGING', areaCode: string) => void
  searchResults: AvailablePhoneNumber[]
  searching: boolean
  purchaseNumber: (input: PurchasePhoneNumberInput) => Promise<boolean>
  releaseNumber: (id: string) => Promise<boolean>
  updateRoles: (id: string, roles: string[]) => Promise<boolean>
  refetch: () => void
}

export function useManagedNumbers(locationId: string): UseManagedNumbersReturn {
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])
  const searchFilter = `locationId:${locationId} AND (provisioningStatus:active OR provisioningStatus:ordering OR provisioningStatus:failed)`

  const [queryResult, reexecute] = useQuery({
    query: PHONE_NUMBERS_QUERY,
    variables: { search: searchFilter, pagination: { limit: 100 } },
    pause: !locationId,
  })

  const [searchResult, executeSearch] = useQuery({
    query: SEARCH_AVAILABLE_QUERY,
    variables: { type: 'VOICE' as const, areaCode: '', limit: 5 },
    pause: true,
    context: opsContext,
  })

  const [, executePurchase] = useMutation(PURCHASE_MUTATION)
  const [, executeRelease] = useMutation(RELEASE_MUTATION)
  const [, executeUpdate] = useMutation(UPDATE_ROLES_MUTATION)

  const phoneNumbers: PhoneNumberHit[] = queryResult.data?.phoneNumbers?.hits ?? []

  const searchAvailable = useCallback(
    (type: 'VOICE' | 'MESSAGING', areaCode: string) => {
      executeSearch({ type, areaCode, limit: 5 })
    },
    [executeSearch],
  )

  const searchResults: AvailablePhoneNumber[] = searchResult.data?.searchAvailablePhoneNumbers ?? []

  const purchaseNumber = useCallback(
    async (input: PurchasePhoneNumberInput): Promise<boolean> => {
      const { data, error } = await executePurchase({ input }, opsContext)
      if (error) {
        logError('purchaseNumber failed', error)
        notifyError(error.message)
        return false
      }
      const status = data?.purchasePhoneNumber?.provisioningStatus
      if (status === 'ordering') {
        notifySuccess('Number ordered — provisioning in progress')
      } else {
        notifySuccess('Number purchased')
      }
      reexecute({ requestPolicy: 'network-only' })
      return true
    },
    [executePurchase, opsContext, reexecute],
  )

  const releaseNumber = useCallback(
    async (id: string): Promise<boolean> => {
      const { error } = await executeRelease({ id }, opsContext)
      if (error) {
        logError('releaseNumber failed', error)
        notifyError(error.message)
        return false
      }
      notifySuccess('Number released')
      reexecute({ requestPolicy: 'network-only' })
      return true
    },
    [executeRelease, opsContext, reexecute],
  )

  const updateRoles = useCallback(
    async (id: string, roles: string[]): Promise<boolean> => {
      const { error } = await executeUpdate({ id, input: { roles } })
      if (error) {
        logError('updateRoles failed', error)
        notifyError(error.message)
        return false
      }
      notifySuccess('Roles updated')
      reexecute({ requestPolicy: 'network-only' })
      return true
    },
    [executeUpdate, reexecute],
  )

  const refetch = useCallback(() => {
    reexecute({ requestPolicy: 'network-only' })
  }, [reexecute])

  return {
    phoneNumbers,
    fetching: queryResult.fetching,
    error: queryResult.error?.message,
    searchAvailable,
    searchResults,
    searching: searchResult.fetching,
    purchaseNumber,
    releaseNumber,
    updateRoles,
    refetch,
  }
}
